import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { ChevronsLeft } from "lucide-react"
import { useEffect, useState, type ReactNode } from "react"
import { usePanelRef, type PanelImperativeHandle } from "react-resizable-panels"
import { useSearchParams } from "react-router"
import { DetailPanelCollapseContext } from "./DetailPanelCollapseContext"

const DEFAULT_DETAIL_SIZE = 45
const MIN_DETAIL_SIZE = 15

// The detail panel's last open width, across mounts. Every entity page mounts
// this layout without a selection and collapses the panel before the list
// auto-selects a row; react-resizable-panels 4 then saves the collapsed layout
// and only remembers the pre-collapse width in memory (v2 persisted it in
// localStorage next to the layout), so without this the split reset to
// DEFAULT_DETAIL_SIZE on every tab switch.
const DETAIL_SIZE_STORAGE_KEY = "mc-master-data-detail-size"

function rememberedDetailSize(): number {
  try {
    const stored = Number(localStorage.getItem(DETAIL_SIZE_STORAGE_KEY))
    if (stored >= MIN_DETAIL_SIZE && stored < 100) return stored
  } catch {
    // Storage unavailable: fall through to the default.
  }
  return DEFAULT_DETAIL_SIZE
}

function rememberDetailSize(size: number) {
  try {
    localStorage.setItem(DETAIL_SIZE_STORAGE_KEY, String(size))
  } catch {
    // Storage unavailable: the split simply is not remembered.
  }
}

// react-resizable-panels 4's expand() takes no size: it restores the width
// from before the collapse (this mount only), else minSize. Selecting a row
// reopens the detail panel at its remembered width, at least
// DEFAULT_DETAIL_SIZE on first use, as expand(size) did in v2.
function expandDetail(panel: PanelImperativeHandle) {
  // Read before expand(): its onResize would overwrite the memory with the
  // interim width.
  const target = rememberedDetailSize()
  panel.expand()
  if (panel.getSize().asPercentage < target) {
    panel.resize(`${target}%`)
  }
}

interface MasterDataPanelLayoutProps {
  listPanel: ReactNode
  detailPanel: ReactNode
}

/**
 * Shared resizable two-panel shell used by all entity pages.
 * The right panel collapses when no ?id= is selected and expands to at least
 * DEFAULT_DETAIL_SIZE when a row is picked.
 */
export function MasterDataPanelLayout({
  listPanel,
  detailPanel,
}: MasterDataPanelLayoutProps) {
  const { translate } = useTranslation()
  const [searchParams] = useSearchParams()
  const hasSelection = !!searchParams.get("id")
  const detailRef = usePanelRef()
  const [isDetailCollapsed, setIsDetailCollapsed] = useState(!hasSelection)

  useEffect(() => {
    const panel = detailRef.current
    if (!panel) return
    // react-resizable-panels' imperative expand/collapse assert the panel has a
    // measured size; before the group is laid out (jsdom tests, SSR, the first
    // paint before ResizeObserver fires) that assertion throws. The call is only
    // cosmetic — `defaultSize` already reflects `hasSelection` — so swallow the
    // pre-measurement error and let the next layout pass settle it.
    try {
      if (hasSelection) {
        expandDetail(panel)
      } else {
        panel.collapse()
      }
    } catch {
      // Panel not measured yet; defaultSize already matches hasSelection.
    }
  }, [hasSelection])

  return (
    <ResizablePanelGroup
      orientation="horizontal"
      autoSaveId="master-data-panels"
      className="relative h-full rounded-xl border border-grey4 bg-grey3"
    >
      <ResizablePanel
        id="list"
        defaultSize={hasSelection ? 100 - DEFAULT_DETAIL_SIZE : 100}
        minSize={20}
      >
        {/* When the detail panel is collapsed, the rail button overlays the list's
            right edge (w-10). Reserve that space so right-aligned content — e.g. the
            status pills — isn't hidden underneath it. */}
        <div className={cn("h-full bg-grey2", isDetailCollapsed && "pr-10")}>
          {listPanel}
        </div>
      </ResizablePanel>
      <ResizableHandle
        withHandle
        disabled={isDetailCollapsed}
        className={isDetailCollapsed ? "hidden" : "border-y-0"}
      />
      <ResizablePanel
        id="detail"
        panelRef={detailRef}
        defaultSize={hasSelection ? DEFAULT_DETAIL_SIZE : 0}
        minSize={MIN_DETAIL_SIZE}
        collapsible
        collapsedSize={0}
        onResize={(size) => {
          const percentage = size.asPercentage
          setIsDetailCollapsed(percentage === 0)
          if (percentage > 0) rememberDetailSize(percentage)
        }}
      >
        {/* A plain scrolling div, not the themed `ScrollArea`: Radix's Viewport
            wraps children in an internal `display:table` element to measure
            natural content width — that sizes to the widest descendant (e.g.
            a multi-column sub-table) regardless of the panel's actual width,
            so nothing in here (flex-wrap included) ever sees a narrowed
            width; it only gets visually clipped once the natural width wins.
            A native overflow container doesn't have that quirk. */}
        {/* `relative`: the Radix Viewport this replaced was a positioned ancestor.
            Detail panels lay out their body with `absolute inset-0` and anchor to the
            nearest positioned ancestor — without this, that is the panel group, and
            the body renders over the list (prod, 2026-09-03). */}
        <div className="relative h-full overflow-y-auto overflow-x-hidden">
          <DetailPanelCollapseContext.Provider
            value={() => detailRef.current?.collapse()}
          >
            {detailPanel}
          </DetailPanelCollapseContext.Provider>
        </div>
      </ResizablePanel>
      {isDetailCollapsed && (
        <button
          type="button"
          onClick={() => {
            if (detailRef.current) expandDetail(detailRef.current)
          }}
          className="absolute inset-y-0 right-0 flex w-10 flex-col items-center gap-2 rounded-r-xl border-l border-grey4 bg-grey3 pt-3 hover:bg-grey4"
        >
          <ChevronsLeft size={14} className="shrink-0 text-grey6" />
          <span
            style={{ writingMode: "vertical-rl" }}
            className="text-xs font-semibold uppercase tracking-widest text-grey6"
          >
            {translate("MC_MD_DETAILS_RAIL_LABEL")}
          </span>
        </button>
      )}
    </ResizablePanelGroup>
  )
}
